import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { useUserRole } from "@/hooks/useUserRole";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import { Heart, MessageCircle, Pin, Trash2, Send, Loader2, Filter } from "lucide-react";
import { Link } from "react-router-dom";

interface CommunityFeedProps {
  limit?: number;
  showComposer?: boolean;
  showFilters?: boolean;
}

interface CommunityPost {
  id: string;
  organization_id: string;
  author_id: string;
  content: string;
  post_type: string;
  is_pinned: boolean;
  likes_count: number;
  comments_count: number;
  image_url: string | null;
  created_at: string;
}

interface CommunityComment {
  id: string;
  post_id: string;
  author_id: string;
  content: string;
  created_at: string;
}

interface ProfileSummary {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
}

export default function CommunityFeed({ limit, showComposer = true, showFilters = true }: CommunityFeedProps) {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { isAdmin } = useUserRole();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<string>("all");
  const [newContent, setNewContent] = useState("");
  const [newType, setNewType] = useState<string>("discussion");
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [commentText, setCommentText] = useState<Record<string, string>>({});

  // Posts
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["community-posts", organization?.id, filter],
    queryFn: async () => {
      let query = supabase
        .from("community_posts" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .order("is_pinned", { ascending: false })
        .order("created_at", { ascending: false });
      if (filter !== "all") {
        query = query.eq("post_type", filter);
      }
      if (limit) {
        query = query.limit(limit);
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data as CommunityPost[] | null) ?? [];
    },
    enabled: !!organization?.id,
  });

  // Realtime subscription for new posts
  useEffect(() => {
    if (!organization?.id) return;
    const channel = supabase
      .channel("community-posts-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "community_posts", filter: `organization_id=eq.${organization.id}` },
        () => {
          qc.invalidateQueries({ queryKey: ["community-posts"] });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [organization?.id, qc]);

  // Profiles for avatars
  const authorIds = [...new Set(posts.map((p) => p.author_id))];
  const { data: profiles = [] } = useQuery({
    queryKey: ["community-profiles", authorIds],
    queryFn: async () => {
      if (authorIds.length === 0) return [];
      const { data } = await supabase.from("public_profiles" as any).select("id, full_name, avatar_url").in("id", authorIds);
      return (data ?? []) as ProfileSummary[];
    },
    enabled: authorIds.length > 0,
  });
  const profileMap = useMemo(() => Object.fromEntries(profiles.map((p) => [p.id, p])), [profiles]);

  // User's likes
  const { data: myLikes = [] } = useQuery({
    queryKey: ["community-my-likes", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("community_likes" as any)
        .select("post_id")
        .eq("user_id", user!.id);
      return ((data as Array<{ post_id: string }>) ?? []).map((l) => l.post_id);
    },
    enabled: !!user?.id,
  });
  const likedSet = useMemo(() => new Set(myLikes), [myLikes]);

  // Create post
  const createMutation = useMutation({
    mutationFn: async () => {
      if (!newContent.trim() || !user || !organization) return;
      const { error } = await supabase.from("community_posts" as any).insert({
        organization_id: organization.id,
        author_id: user.id,
        content: newContent.trim(),
        post_type: newType,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["community-posts"] });
      setNewContent("");
      toast.success("Inlägg publicerat!");
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || "Kunde inte publicera"); },
  });

  // Toggle like (optimistic + upsert to prevent duplicates)
  const likeMutation = useMutation({
    mutationFn: async (postId: string) => {
      if (!user) return;
      const isLiked = likedSet.has(postId);
      if (isLiked) {
        await supabase.from("community_likes" as any).delete().eq("post_id", postId).eq("user_id", user.id);
        await supabase.from("community_posts" as any).update({ likes_count: Math.max(0, (posts.find((p) => p.id === postId)?.likes_count || 1) - 1) }).eq("id", postId);
      } else {
        await supabase.from("community_likes" as any).upsert({ post_id: postId, user_id: user.id }, { onConflict: "post_id,user_id" } as any);
        await supabase.from("community_posts" as any).update({ likes_count: (posts.find((p) => p.id === postId)?.likes_count || 0) + 1 }).eq("id", postId);
      }
    },
    onMutate: async (postId: string) => {
      await qc.cancelQueries({ queryKey: ["community-my-likes"] });
      const prev = qc.getQueryData<string[]>(["community-my-likes", user?.id]) ?? [];
      const isLiked = prev.includes(postId);
      qc.setQueryData(["community-my-likes", user?.id], isLiked ? prev.filter(id => id !== postId) : [...prev, postId]);
      return { prev };
    },
    onError: (_err: unknown, _postId: string, context: { prev: string[] } | undefined) => {
      if (context?.prev) qc.setQueryData(["community-my-likes", user?.id], context.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["community-posts"] });
      qc.invalidateQueries({ queryKey: ["community-my-likes"] });
    },
  });

  // Delete post (admin)
  const deleteMutation = useMutation({
    mutationFn: async (postId: string) => {
      await supabase.from("community_posts" as any).delete().eq("id", postId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["community-posts"] });
      toast.success("Inlägg borttaget");
    },
  });

  // Pin/unpin (admin)
  const pinMutation = useMutation({
    mutationFn: async ({ postId, pinned }: { postId: string; pinned: boolean }) => {
      await supabase.from("community_posts" as any).update({ is_pinned: pinned }).eq("id", postId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["community-posts"] }),
  });

  // Comments
  const { data: allComments = [] } = useQuery({
    queryKey: ["community-comments", organization?.id],
    queryFn: async () => {
      const postIds = posts.map((p) => p.id);
      if (postIds.length === 0) return [];
      const { data } = await supabase
        .from("community_comments" as any)
        .select("*")
        .in("post_id", postIds)
        .order("created_at", { ascending: true });
      return (data as CommunityComment[] | null) ?? [];
    },
    enabled: posts.length > 0,
  });

  // Comment profiles
  const commentAuthorIds = [...new Set(allComments.map((c) => c.author_id))];
  const { data: commentProfiles = [] } = useQuery({
    queryKey: ["community-comment-profiles", commentAuthorIds],
    queryFn: async () => {
      if (commentAuthorIds.length === 0) return [];
      const { data } = await supabase.from("public_profiles" as any).select("id, full_name").in("id", commentAuthorIds);
      return (data ?? []) as Array<{ id: string; full_name: string | null }>;
    },
    enabled: commentAuthorIds.length > 0,
  });
  const commentProfileMap = useMemo(() => Object.fromEntries(commentProfiles.map((p) => [p.id, p])), [commentProfiles]);

  const commentMutation = useMutation({
    mutationFn: async (postId: string) => {
      const text = commentText[postId]?.trim();
      if (!text || !user) return;
      await supabase.from("community_comments" as any).insert({
        post_id: postId,
        author_id: user.id,
        content: text,
      });
      // Update count
      const post = posts.find((p) => p.id === postId);
      await supabase.from("community_posts" as any).update({ comments_count: (post?.comments_count || 0) + 1 }).eq("id", postId);
    },
    onSuccess: (_, postId) => {
      qc.invalidateQueries({ queryKey: ["community-posts"] });
      qc.invalidateQueries({ queryKey: ["community-comments"] });
      setCommentText((prev) => ({ ...prev, [postId]: "" }));
    },
  });

  const typeLabels: Record<string, string> = {
    discussion: "Diskussion",
    question: "Fråga",
    news: "Nyhet",
    open_match_share: "Aktivitet",
  };

  const typeColors: Record<string, string> = {
    discussion: "default",
    question: "secondary",
    news: "default",
    open_match_share: "outline",
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      {showFilters && (
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla</SelectItem>
              <SelectItem value="discussion">Diskussioner</SelectItem>
              <SelectItem value="question">Frågor</SelectItem>
              <SelectItem value="news">Nyheter</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Composer */}
      {showComposer && (
        <Card>
          <CardContent className="p-3 space-y-2">
            <Textarea
              placeholder="Skriv ett inlägg..."
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              className="min-h-[60px] text-sm"
            />
            <div className="flex items-center justify-between">
              <Select value={newType} onValueChange={setNewType}>
                <SelectTrigger className="w-32 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="discussion">Diskussion</SelectItem>
                  <SelectItem value="question">Fråga</SelectItem>
                  {isAdmin && <SelectItem value="news">Nyhet</SelectItem>}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={() => createMutation.mutate()}
                disabled={!newContent.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                Publicera
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Posts */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground text-center py-4">Laddar...</p>
      ) : posts.length === 0 ? (
        <div className="text-center py-8 space-y-3">
          <p className="text-sm text-muted-foreground">Inga inlägg ännu. Bli den första att dela något! 🎾</p>
          {!showComposer && (
            <Button variant="outline" size="sm" asChild>
              <Link to="/community">Gå till Community →</Link>
            </Button>
          )}
        </div>
      ) : (
        posts.map((post) => {
          const author = profileMap[post.author_id];
          const initials = (author?.full_name || "?").split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2);
          const isLiked = likedSet.has(post.id);
          const isExpanded = expandedComments.has(post.id);
          const postComments = allComments.filter((c) => c.post_id === post.id);

          return (
            <Card key={post.id} className={post.is_pinned ? "border-primary/30 bg-primary/5" : ""}>
              <CardContent className="p-3 space-y-2">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{author?.full_name || "Okänd"}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: sv })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {post.is_pinned && <Pin className="h-3.5 w-3.5 text-primary" />}
                    <Badge variant={typeColors[post.post_type] as "default" | "secondary" | "outline" | "destructive"} className="text-[10px]">
                      {typeLabels[post.post_type] || post.post_type}
                    </Badge>
                  </div>
                </div>

                {/* Content */}
                <p className="text-sm whitespace-pre-wrap">{post.content}</p>
                {post.image_url && (
                  <img src={post.image_url} alt="" className="rounded-lg max-h-64 object-cover w-full" />
                )}

                {/* Actions */}
                <div className="flex items-center gap-3 pt-1">
                  <button
                    onClick={() => likeMutation.mutate(post.id)}
                    className={`flex items-center gap-1 text-xs transition-colors ${isLiked ? "text-red-500" : "text-muted-foreground hover:text-red-500"}`}
                  >
                    <Heart className={`h-4 w-4 ${isLiked ? "fill-current" : ""}`} />
                    {post.likes_count > 0 && <span>{post.likes_count}</span>}
                  </button>
                  <button
                    onClick={() => setExpandedComments((prev) => {
                      // UPPDRAG-30: ternary-as-statement → if/else for lint clarity.
                      const next = new Set(prev);
                      if (next.has(post.id)) next.delete(post.id);
                      else next.add(post.id);
                      return next;
                    })}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {post.comments_count > 0 && <span>{post.comments_count}</span>}
                  </button>
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => pinMutation.mutate({ postId: post.id, pinned: !post.is_pinned })}
                        className="text-xs text-muted-foreground hover:text-foreground ml-auto"
                        title={post.is_pinned ? "Ta bort pin" : "Pinna"}
                      >
                        <Pin className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => deleteMutation.mutate(post.id)}
                        className="text-xs text-destructive hover:text-destructive/80"
                        title="Ta bort"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>

                {/* Comments — show last 2 by default, all when expanded */}
                {postComments.length > 0 && (
                  <div className="border-t pt-2 space-y-2">
                    {postComments.length > 2 && !isExpanded && (
                      <button
                        className="text-xs text-primary hover:underline"
                        onClick={() => setExpandedComments((prev) => {
                          const next = new Set(prev);
                          next.add(post.id);
                          return next;
                        })}
                      >
                        Visa alla {postComments.length} kommentarer
                      </button>
                    )}
                    {(isExpanded ? postComments : postComments.slice(-2)).map((c) => {
                      const cAuthor = commentProfileMap[c.author_id];
                      return (
                        <div key={c.id} className="flex gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-[9px]">
                              {(cAuthor?.full_name || "?")[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs">
                              <span className="font-medium">{cAuthor?.full_name || "Okänd"}</span>{" "}
                              <span className="text-muted-foreground">{c.content}</span>
                            </p>
                            <p className="text-[10px] text-muted-foreground">
                              {formatDistanceToNow(new Date(c.created_at), { addSuffix: true, locale: sv })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Skriv en kommentar..."
                        value={commentText[post.id] || ""}
                        onChange={(e) => setCommentText((prev) => ({ ...prev, [post.id]: e.target.value }))}
                        className="h-8 text-xs"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            commentMutation.mutate(post.id);
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2"
                        onClick={() => commentMutation.mutate(post.id)}
                        disabled={!commentText[post.id]?.trim()}
                      >
                        <Send className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}
